import { useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useAdmin } from '../../hooks/useAdmin'
import { useCompany } from '../../hooks/useCompany'
import { useNewsletterSubscribe } from '../../hooks/useNewsletterSubscribe'

const Newsletter = () => {
  const { user, loading, signOut } = useAuth()
  const { subscribe } = useAdmin()
  const { subscribeAnonymousAsync, subscribeAnonymousLoading } = useNewsletterSubscribe()
  const { settings } = useCompany()
  const [email, setEmail] = useState('')
  const [submitError, setSubmitError] = useState('')
  
  // Check if newsletter feature is enabled (defaults to true if not configured)
  const isNewsletterEnabled = settings?.features?.newsletter !== false
  
  // If feature is disabled, don't render
  if (!isNewsletterEnabled) {
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError('')
    
    if (!user) {
      try {
        await subscribeAnonymousAsync(email)
        setEmail('')
        alert('Thank you! You have been subscribed. Check your email for confirmation.')
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Subscription failed'
        setSubmitError(msg)
      }
      return
    }

    try {
      await subscribe(email)
      setEmail('')
      alert('Thank you! You have been subscribed.')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to subscribe'
      setSubmitError(msg)
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (err: any) {
      alert(`Failed to sign out: ${err.message}`)
    }
  }

  return (
    <section
      className="py-16 bg-white relative overflow-hidden border-t border-gray-100 scroll-mt-20"
      id="executive-briefings"
      style={{
        backgroundImage: 'url(/assets/newsletter-background.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <h2 className="text-2xl font-bold mb-4">Subscribe To Our Executive Briefings</h2>
        <p className="text-gray-600 mb-8">
          We don't just deliver data—we deliver intelligence that matters. Whether you're navigating market volatility, driving innovation, or planning your next big move, we equip you with the clarity, foresight, and confidence to lead.
        </p>

        {loading ? (
          <div className="text-center py-4">
            <i className="fa-solid fa-spinner fa-spin text-gray-400 text-2xl"></i>
          </div>
        ) : !user ? (
          <div className="animate-fade-in-up">
            <form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setSubmitError('')
                }}
                placeholder="Enter your email"
                required
                disabled={subscribeAnonymousLoading}
                className="flex-grow px-5 py-3 bg-gray-50 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-black focus:bg-white transition-all text-gray-900 placeholder-gray-400 disabled:opacity-70"
              />
              <button
                type="submit"
                disabled={subscribeAnonymousLoading}
                className="px-8 py-3 bg-black text-white font-bold uppercase rounded-full hover:bg-gray-800 transition-colors shadow-lg whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {subscribeAnonymousLoading ? 'Subscribing…' : 'Subscribe to Briefings'}
              </button>
            </form>
            {submitError && (
              <p className="mt-3 text-sm text-red-600" role="alert">
                {submitError}
              </p>
            )}
          </div>
        ) : (
          <div className="text-center animate-fade-in-up">
            <p className="text-gray-600 mb-4">You're signed in as {user.email}</p>
            <button
              onClick={handleSignOut}
              className="px-8 py-3 bg-black text-white font-bold uppercase rounded-full hover:bg-gray-800 transition-colors shadow-lg"
            >
              Sign Out
            </button>
          </div>
        )}
      </div>
    </section>
  )
}

export default Newsletter
